import { useEffect, useState } from 'react';
import { api, describeError } from '../lib/api';
import { ScreenMessage } from './ui/ScreenMessage';

/** Start the monday OAuth flow: the backend answers with the URL monday should send the admin to. */
export async function startMondayConnect(): Promise<void> {
  const { authorizeUrl } = await api.mondayConnect();
  window.location.assign(authorizeUrl);
}

export function MondayNotConnected({ isAdmin }: { isAdmin: boolean }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const connect = async () => {
    setBusy(true);
    setError(null);
    try {
      await startMondayConnect();
    } catch (e) {
      setError(describeError(e));
      setBusy(false);
    }
  };
  return (
    <ScreenMessage
      title="monday is not connected"
      tone="error"
      detail={isAdmin ? 'Connect monday to load and edit the roadmap boards.' : 'Ask an admin to connect monday, then reload this page.'}
      action={isAdmin ? { label: busy ? 'Redirecting...' : 'Connect monday', onClick: () => void connect() } : { label: 'Reload', onClick: () => window.location.reload() }}
    >
      {error ? (
        <p className="mt-2 text-[14px] text-danger" role="alert">
          {error}
        </p>
      ) : null}
    </ScreenMessage>
  );
}

type CallbackState = { phase: 'working' } | { phase: 'done' } | { phase: 'error'; message: string };

let callbackRun: Promise<CallbackState> | null = null;

/** Handles /monday/callback?code=..&state=.. (or ?error=..): forwards code+state to the backend, which validates them. */
function runCallback(): Promise<CallbackState> {
  callbackRun ??= (async (): Promise<CallbackState> => {
    const p = new URLSearchParams(window.location.search);
    const error = p.get('error');
    if (error) return { phase: 'error', message: p.get('error_description') ?? error };
    const code = p.get('code');
    const state = p.get('state');
    if (!code || !state) return { phase: 'error', message: 'The monday redirect did not include a code and state.' };
    try {
      await api.mondayCallback({ code, state });
      return { phase: 'done' };
    } catch (e) {
      return { phase: 'error', message: describeError(e) };
    }
  })();
  return callbackRun;
}

export function MondayCallbackPage() {
  const [state, setState] = useState<CallbackState>({ phase: 'working' });
  useEffect(() => {
    let live = true;
    void runCallback().then((s) => live && setState(s));
    return () => {
      live = false;
    };
  }, []);
  useEffect(() => {
    if (state.phase !== 'done') return;
    const t = setTimeout(() => window.location.assign('/'), 1500);
    return () => clearTimeout(t);
  }, [state.phase]);

  if (state.phase === 'working') return <ScreenMessage title="Connecting monday" detail="Finishing the connection..." busy />;
  if (state.phase === 'done') return <ScreenMessage title="monday connected" detail="Taking you back to the roadmap..." action={{ label: 'Open roadmap', onClick: () => window.location.assign('/') }} />;
  return <ScreenMessage title="Could not connect monday" tone="error" detail={state.message} action={{ label: 'Back to roadmap', onClick: () => window.location.assign('/') }} />;
}
